import { Badge } from '~/components/ui/badge'
import { VerdictBadge } from '~/components/assessment/assessment-badges'
import type { ChecklistItem, ItemScore, SurveyAnswer } from '~/api/types'
import {
    answerRows,
    formatChecklistItemLabel,
    itemCategoryPath,
    resultMethodLabel,
    splitChecklistDetail,
} from './utils'

export function ChecklistResultDetail({
    item,
    score,
    answers,
}: {
    item: ChecklistItem
    score: ItemScore
    answers: SurveyAnswer[]
}) {
    const sections = splitChecklistDetail(item)
    const rows = answerRows(item, answers)

    return (
        <div className='space-y-4'>
            <div>
                <div className='mb-2 flex flex-wrap items-center gap-1.5'>
                    <Badge tone='neutral'>{item.classification}</Badge>
                    <Badge tone='info'>{item.maxScore}점 항목</Badge>
                    {score.verdict ? (
                        <VerdictBadge verdict={score.verdict} />
                    ) : null}
                </div>
                <h3 className='text-foreground text-base leading-relaxed font-semibold'>
                    {formatChecklistItemLabel(item)}
                </h3>
                <p className='text-muted-foreground mt-1 text-sm'>
                    {itemCategoryPath(item) || '분류 정보 없음'}
                </p>
                <p className='text-muted-foreground mt-2 text-sm'>
                    점수 {score.score ?? 0}/{score.maxScore} ·{' '}
                    {resultMethodLabel(score.method)}
                </p>
            </div>

            {/* 채점 근거(HACCP 기준 명시) — 왜 이 점수·판정인지. */}
            <div className='border-border bg-muted/40 rounded-md border p-3'>
                <p className='text-foreground text-xs font-semibold'>
                    채점 근거
                </p>
                <p className='text-muted-foreground mt-1.5 text-xs leading-relaxed'>
                    {score.method === 'deterministic'
                        ? '선행요건 — 평가로직(수집값 조건)을 규칙으로 판정했습니다. 모든 조건 충족 시 배점 만점(적합), 일부만 충족하면 부분 점수(미흡), 충족 없음은 0점(부적합)입니다.'
                        : 'HACCP관리 — 배점기준(척도)에 수집 근거를 대입해 AI가 0~만점 밴드를 매겼습니다.'}
                </p>
                {score.reasoning ? (
                    <p className='text-foreground mt-2 text-sm leading-relaxed whitespace-pre-wrap'>
                        {score.reasoning}
                    </p>
                ) : null}
                {score.needsReview ? (
                    <p className='text-warning mt-2 text-xs leading-relaxed'>
                        ※ 텍스트 기준·미응답 등으로 규칙이 자동 판정하기 어려운
                        항목입니다. 사람 검토를 권장합니다.
                    </p>
                ) : null}
            </div>

            <dl className='border-border bg-muted/40 grid gap-2 rounded-md border p-3 text-sm sm:grid-cols-2'>
                <div>
                    <dt className='text-muted-foreground text-xs'>PART</dt>
                    <dd className='text-foreground mt-0.5 font-medium'>
                        {item.part || '-'}
                    </dd>
                </div>
                <div>
                    <dt className='text-muted-foreground text-xs'>대분류</dt>
                    <dd className='text-foreground mt-0.5 font-medium'>
                        {item.majorCategory || '-'}
                    </dd>
                </div>
                <div>
                    <dt className='text-muted-foreground text-xs'>중분류</dt>
                    <dd className='text-foreground mt-0.5 font-medium'>
                        {item.middleCategory || '-'}
                    </dd>
                </div>
                <div>
                    <dt className='text-muted-foreground text-xs'>채점 방식</dt>
                    <dd className='text-foreground mt-0.5 font-medium'>
                        {resultMethodLabel(score.method)}
                    </dd>
                </div>
            </dl>

            <section>
                <h4 className='text-foreground text-sm font-semibold'>
                    나의 답변
                </h4>
                <div className='mt-2 space-y-2'>
                    {rows.map((row) => (
                        <div
                            key={row.key}
                            className='border-border rounded-md border p-3'>
                            <p className='text-muted-foreground text-xs'>
                                {row.label}
                            </p>
                            {row.question ? (
                                <p className='text-muted-foreground mt-1 text-xs leading-relaxed'>
                                    {row.question}
                                </p>
                            ) : null}
                            <p className='text-foreground mt-2 text-sm font-medium whitespace-pre-wrap'>
                                {row.value}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            <section>
                <h4 className='text-foreground text-sm font-semibold'>
                    기준·관리·현장 정보
                </h4>
                {sections.length > 0 ? (
                    <div className='mt-2 space-y-2'>
                        {sections.map((section) => (
                            <div
                                key={`${section.title}-${section.content.slice(0, 16)}`}
                                className='border-border rounded-md border p-3'>
                                <p className='text-primary text-sm font-semibold'>
                                    {section.title}
                                </p>
                                <p className='text-foreground mt-2 text-sm leading-relaxed whitespace-pre-line'>
                                    {section.content}
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className='text-muted-foreground mt-2 text-sm'>
                        등록된 기준·관리·현장 정보가 없습니다.
                    </p>
                )}
            </section>
        </div>
    )
}
